import { isTeacherLoopEnabled } from '../../utils/teacherLoopGate';

/**
 * Teacher Loop (board B2m): the logged-out pages a phone may open even while
 * the mobile experience (REACT_APP_MOBILE_EXPERIENCE) is dark.
 *
 *   /check/<token>          a teacher reviewing a student's draft
 *   /check/verify?v=…       the confirmation link from the teacher's email
 *   /view/submissions/<id>  a shared draft
 *
 * Today a phone gets the "Open our web app in desktop" wall for these, and a
 * teacher's first tap on a student's link dead-ends there. With the Teacher
 * Loop build flag on, App renders the existing mobile feedback view for these
 * routes only (MobileGate `publicOnly`); every other phone route keeps the wall.
 *
 * Kept beside ReactiveRender on purpose: it is a sibling of
 * isMobileWriteWorkflowPath, but useMobileHandoffView does not read it (the
 * hold, the phone check and the flag gating are unaffected).
 * With the flag off this is always false, so nothing anywhere changes.
 */
export const MOBILE_PUBLIC_ROUTE_PATTERNS = Object.freeze([
  /^\/check\/[^/]+\/?$/,
  /^\/view\/submissions\/[^/]+\/?$/,
]);

const normalisePath = (path) => {
  const value = String(path || '/').replace(/^#/, '');
  const pathname = value.split(/[?#]/)[0] || '/';
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
};

// The app routes by hash (HashRouter), so the route is the hash. A path-form
// link (/check/<token>, before the Netlify edge function moves it onto the
// hash) has no route inside the app yet and must not turn the shell on.
const currentHashRoute = () => {
  if (typeof window === 'undefined') return '/';
  return window.location.hash || '/';
};

export function isMobilePublicRoute(
  path = currentHashRoute(),
  enabled = isTeacherLoopEnabled()
) {
  if (!enabled) return false;
  const pathname = normalisePath(path);
  return MOBILE_PUBLIC_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname));
}

/**
 * Teacher Loop gift pages (boards G3, G6): the thank-you card a teacher opens
 * from a student's message (/thanks/<code>) and the link in their confirmation
 * email (/gift/verify?v=…). Teachers open these on phones as often as on
 * laptops, so they are their own responsive pages: on a phone App renders them
 * in place of the "use desktop" wall and never inside the mobile shell. With
 * the flag off this is always false.
 */
export const RESPONSIVE_PUBLIC_ROUTE_PATTERNS = Object.freeze([
  /^\/thanks\/[^/]+\/?$/,
  /^\/gift\/verify\/?$/,
]);

export function isResponsivePublicRoute(
  path = currentHashRoute(),
  enabled = isTeacherLoopEnabled()
) {
  if (!enabled) return false;
  const pathname = normalisePath(path);
  return RESPONSIVE_PUBLIC_ROUTE_PATTERNS.some((pattern) =>
    pattern.test(pathname)
  );
}
